export const CARD_WIDTH = 900;
export const CARD_HEIGHT = 1260;
export const SAMPLE_PHOTO_URL = '/my-card/sample-cat.png';
export const MAX_PHOTO_BYTES = 15 * 1024 * 1024;
export type CardStyle = 'battle' | 'holo' | 'retro' | 'sticker';
export type CardTheme = 'daily' | 'office' | 'pet';
export type CardRarity = 'R' | 'SR' | 'SSR';

export const CARD_STYLES: readonly { id: CardStyle; title: string; subtitle: string }[] = [
  { id: 'battle', title: '스타 배틀', subtitle: '별 등급부터 필살기까지, 나만의 수집 카드' },
  { id: 'holo', title: '홀로그램', subtitle: '빛나는 나의 등장' },
  { id: 'retro', title: '레트로 게임', subtitle: '오늘의 플레이어' },
  { id: 'sticker', title: '키치 스티커', subtitle: '귀여움 한 장 가득' },
];

export const CARD_THEMES: readonly { id: CardTheme; title: string }[] = [
  { id: 'daily', title: '그냥 나' }, { id: 'office', title: '직장인' }, { id: 'pet', title: '반려동물' },
];

const THEMES: Record<CardTheme, { stats: [string, string, string]; titles: string[]; skills: string[] }> = {
  daily: {
    stats: ['매력', '눈치', '행동력'],
    titles: ['존재감 만렙', '느긋한 주인공', '평범함은 거절', '은근한 인기쟁이', '오늘의 히든 캐릭터'],
    skills: ['아무것도 안 해도 존재감 발산', '웃음 한 번으로 분위기 전환', '최애 얘기에는 체력 무한', '집에 도착하면 모든 능력 회복', '좋아하는 일 앞에서는 즉시 각성'],
  },
  office: {
    stats: ['집중력', '생존력', '퇴근 감지'],
    titles: ['전설의 칼퇴러', '커피로 움직이는 자', '회의실의 생존자', '월요일의 희귀종', '조용한 실력자'],
    skills: ['퇴근 5분 전 이동 속도 두 배', '커피 한 잔으로 체력 즉시 회복', '회의 중 눈빛만으로 의사소통', '점심 메뉴 결정 속도 세계 최강', '월급날에만 모든 능력치 상승'],
  },
  pet: {
    stats: ['귀여움', '먹성', '집사 지배'],
    titles: ['우리 집 최종 보스', '숨 쉬는 귀여움', '간식 탐지 전문가', '낮잠의 지배자', '집사의 유일한 약점'],
    skills: ['눈 한 번 마주치면 간식 획득', '자는 모습만으로 집사 무력화', '봉지 소리 감지 범위 무한대', '골골 한 번에 스트레스 삭제', '이유 없이 귀여워도 효과 발동'],
  },
};

export interface CardIdentity {
  name: string;
  theme: CardTheme;
  skill: string;
  story?: string;
  element?: BattleElement;
}

export type BattleElement = 'light' | 'fire' | 'water' | 'nature';
export const BATTLE_ELEMENTS: readonly { id: BattleElement; title: string; color: string; dark: string; pale: string }[] = [
  { id: 'light', title: '빛', color: '#c29121', dark: '#563a18', pale: '#fff6cd' },
  { id: 'fire', title: '불꽃', color: '#db582c', dark: '#67261d', pale: '#ffebd5' },
  { id: 'water', title: '물', color: '#288ec1', dark: '#163b63', pale: '#dcf4ff' },
  { id: 'nature', title: '풀', color: '#508e55', dark: '#234d39', pale: '#e3f2d8' },
];

const BATTLE_COPY: Record<CardTheme, { moves: [string, string][]; passive: [string, string]; weakness: string; resistance: string; story: string }> = {
  daily: {
    moves: [['분위기 뒤집기', '어색한 공기를 웃음으로 바꾼다. 아군의 기분과 자신감을 함께 회복한다.'], ['최애 모드 ON', '좋아하는 이야기가 나오면 즉시 각성한다. 이번 턴에는 지치지 않는다.'], ['집순이 결계', '나만의 공간에서 에너지를 충전한다. 다음 행동의 위력이 두 배가 된다.']],
    passive: ['은근한 존재감', '가만히 있어도 시선이 모인다. 함께 있는 아군의 매력이 조금씩 상승한다.'],
    weakness: '월요일 ×2', resistance: '칭찬 +30', story: '평범한 하루에 숨어 있는 희귀한 주인공. 친해질수록 새로운 능력이 발견된다고 한다.',
  },
  office: {
    moves: [['칼퇴 대시', '퇴근 시간이 다가오면 이동 속도가 폭발한다. 남은 업무를 한 번에 정리한다.'], ['커피 충전', '커피 한 잔으로 집중력을 회복한다. 다음 회의에서 졸음 공격을 무효화한다.'], ['회의 탈출', '정확한 한마디로 긴 회의를 끝낸다. 아군 모두에게 자유 시간 한 턴을 준다.']],
    passive: ['월급의 수호자', '어떤 월요일에도 출근하는 끈기. 월급날에는 모든 능력치가 상승한다.'],
    weakness: '야근 ×2', resistance: '커피 +30', story: '사무실 어딘가에서 조용히 경험치를 쌓는 중. 퇴근 직전 진정한 실력을 드러내는 타입이다.',
  },
  pet: {
    moves: [['심쿵 눈맞춤', '눈을 마주친 집사의 방어력이 무너진다. 간식을 하나 더 얻을 확률이 높아진다.'], ['간식 레이더', '봉지 소리를 놓치지 않는다. 어디에 숨겨 둔 간식도 빠르게 찾아낸다.'], ['낮잠 회복', '햇볕이 드는 자리에서 체력을 회복한다. 잠든 동안에도 귀여움은 계속 발동한다.']],
    passive: ['집사 자동 조종', '아무것도 하지 않아도 돌봄을 받는다. 귀여운 표정만으로 집사의 행동을 바꾼다.'],
    weakness: '목욕 ×2', resistance: '쓰다듬 +30', story: '작은 발로 집 전체를 지배하는 희귀 생명체. 낮에는 잠들어 있지만 간식 소리에는 즉시 깨어난다.',
  },
};

export interface BattleDetails {
  stars: number; level: number; hp: number; attack: number; defense: number; element: BattleElement;
  passive: { name: string; description: string };
  move: { name: string; description: string; power: number; cost: number };
  weakness: string; resistance: string; story: string;
}

export interface CardResult {
  seed: number;
  rarity: CardRarity;
  serial: string;
  title: string;
  defaultSkill: string;
  stats: [number, number, number];
  labels: [string, string, string];
  battle: BattleDetails;
}

export interface PhotoCrop { zoom: number; x: number; y: number }
export const DEFAULT_CROP: PhotoCrop = { zoom: 1, x: .5, y: .12 };

export function trimCardText(value: string, max: number): string {
  const clean = value.normalize('NFC').replace(/[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/g, ' ').replace(/\s+/g, ' ').trim();
  return Array.from(new Intl.Segmenter('ko', { granularity: 'grapheme' }).segment(clean), (part) => part.segment).slice(0, max).join('');
}

export function makeCardResult(seed: number, theme: CardTheme): CardResult {
  let state = seed >>> 0;
  const random = () => {
    state += 0x6d2b79f5;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
  const roll = random();
  const rarity: CardRarity = roll < .12 ? 'SSR' : roll < .45 ? 'SR' : 'R';
  const data = THEMES[theme];
  const floor = rarity === 'SSR' ? 70 : rarity === 'SR' ? 50 : 25;
  const base = {
    seed, rarity, serial: (seed >>> 0).toString(16).toUpperCase().padStart(8, '0'),
    title: data.titles[Math.floor(random() * data.titles.length)],
    defaultSkill: data.skills[Math.floor(random() * data.skills.length)],
    stats: Array.from({ length: 3 }, () => floor + Math.floor(random() * (100 - floor))) as [number, number, number],
    labels: [...data.stats],
  };
  const copy = BATTLE_COPY[theme];
  const move = copy.moves[Math.floor(random() * copy.moves.length)];
  const stars = rarity === 'SSR' ? 5 : rarity === 'SR' ? 4 : 3;
  return { ...base, labels: [...data.stats], battle: {
    stars, level: Math.min(99, stars * 10 + Math.floor(random() * 40)),
    hp: 60 + stars * 20 + Math.floor(random() * 6) * 10,
    attack: base.stats[0] * 50, defense: base.stats[1] * 50,
    element: BATTLE_ELEMENTS[Math.floor(random() * BATTLE_ELEMENTS.length)].id,
    passive: { name: copy.passive[0], description: copy.passive[1] },
    move: { name: move[0], description: move[1], power: stars * 20 + Math.floor(random() * 4) * 10, cost: stars - 1 },
    weakness: copy.weakness, resistance: copy.resistance, story: copy.story,
  } };
}

export function photoFrameSize(style: CardStyle) {
  if (style === 'battle') return { width: 772, height: 440 };
  if (style === 'retro') return { width: 622, height: 582 };
  if (style === 'sticker') return { width: 666, height: 574 };
  return { width: 780, height: 640 };
}

export function panPhotoCrop(width: number, height: number, frameWidth: number, frameHeight: number, crop: PhotoCrop, dx: number, dy: number): PhotoCrop {
  const rect = photoSourceRect(width, height, frameWidth, frameHeight, crop);
  const scale = frameWidth / rect.sw;
  const clamp = (value: number) => Math.max(0, Math.min(1, value));
  return { ...crop,
    x: width - rect.sw > .0001 ? clamp(crop.x - dx / scale / (width - rect.sw)) : .5,
    y: height - rect.sh > .0001 ? clamp(crop.y - dy / scale / (height - rect.sh)) : .5,
  };
}

export function zoomPhotoCrop(width: number, height: number, frameWidth: number, frameHeight: number, crop: PhotoCrop, zoom: number): PhotoCrop {
  const before = photoSourceRect(width, height, frameWidth, frameHeight, crop);
  const next = { ...crop, zoom: Math.max(1, Math.min(3, zoom)) };
  const after = photoSourceRect(width, height, frameWidth, frameHeight, next);
  const clamp = (value: number) => Math.max(0, Math.min(1, value));
  return { ...next,
    x: width - after.sw > .0001 ? clamp((before.sx + before.sw / 2 - after.sw / 2) / (width - after.sw)) : .5,
    y: height - after.sh > .0001 ? clamp((before.sy + before.sh / 2 - after.sh / 2) / (height - after.sh)) : .5,
  };
}

export function photoSourceRect(width: number, height: number, targetWidth: number, targetHeight: number, crop: PhotoCrop) {
  const zoom = Math.max(1, Math.min(3, Number.isFinite(crop.zoom) ? crop.zoom : 1));
  const scale = Math.max(targetWidth / width, targetHeight / height) * zoom;
  const sw = targetWidth / scale;
  const sh = targetHeight / scale;
  return { sx: (width - sw) * Math.max(0, Math.min(1, crop.x)), sy: (height - sh) * Math.max(0, Math.min(1, crop.y)), sw, sh };
}

export function photoValidation(file: Pick<File, 'size' | 'type'>): string | null {
  if (file.size === 0) return '비어 있는 사진 파일이에요. 다른 사진을 골라 주세요.';
  if (file.size > MAX_PHOTO_BYTES) return '15MB 이하의 사진을 골라 주세요.';
  if (!['image/jpeg', 'image/png', 'image/webp', 'image/avif'].includes(file.type)) return 'JPG, PNG, WebP, AVIF 사진을 골라 주세요. HEIC 사진은 JPG로 바꿔 주세요.';
  return null;
}
